module Sm.Service.Session

open System
open Browser
open MobF

open Sm.Core.Date

open Sm.Service.App
open Sm.Service.Storage
open Sm.Service.Repository
open Sm.Service.Remote
open Sm.Service.Remote.Synchronizer

type Context = {
    User: User
    Environment: Environment
    Repository: Repository
    Synchronizer: SynchronizerType
    ErrorReporter: ErrorReporter
}

type State =
    | Inactive
    | Resuming
    | Active of Context
    | Blocked

type Msg =
    | Tick
    | Login of User
    | Refresh of User
    | Logout
    | Activate of Context
    | Deactivate of Context
    | Block
    | HandleOnline
    | HandleVisible
    | Reboot

type SessionModel = Model<State, Msg>

let create client : SessionModel =
    let init post =
        window.addEventListener("online", fun _ -> post HandleOnline)

        document.addEventListener("visibilitychange", fun _ ->
            match document.visibilityState with
            | "visible" -> post HandleVisible
            | _ -> ()
        )

        match client |> User.current with
        | None -> Inactive
        | Some user -> 
            post (Login user)
            Resuming

    let update (state, post) = function
        | Tick ->
            match state with
            | Active ctx ->
                if ctx.User.ValidAt.HasElapsed(TimeSpan.OneDay) then
                    promise {
                        let! user = User.refresh client
                        post (Refresh user)
                    }
                    |> Promise.start

                if navigator.onLine then
                    let msg =
                        match ctx.Synchronizer.Computed.Status with
                        | Offline -> Some Restart
                        | HaltedSince (d, _) when d.HasElapsed(TimeSpan.OneMinute) -> Some Restart
                        | UpToDate d when d.HasElapsed(TimeSpan.OneHour) -> Some FullSync
                        | _ -> None

                    msg |> Option.iter ctx.Synchronizer.Post

                state
            | _ -> state

        | Login user ->
            promise {
                try
                    let! env = Environment.establish user.Id
                    
                    let repository = new Repository(env)
                    do! repository.Start()

                    let syncServer = PocketbaseSyncServer(env.ClientId, client)
                    let sync = Synchronizer.create Options.Default env syncServer repository
                    let reporter = ErrorReporter(env, client)

                    let ctx = {
                        User = user
                        Environment = env
                        Repository = repository
                        Synchronizer = sync
                        ErrorReporter = reporter
                    }

                    post (Activate ctx)
                with e ->
                    console.error(e)
                    post Block
            }
            |> Promise.start
            state

        | Refresh user ->
            match state with
            | Active ctx -> Active { ctx with User = user }
            | _ -> state

        | Logout ->
            post Reboot

            match state with
            | Active ctx -> post (Deactivate ctx)
            | _ -> ()

            client |> User.signout
            state

        | Activate ctx ->
            ctx.Synchronizer.Post(Start)
            ctx.ErrorReporter.Start()

            Active ctx

        | Deactivate ctx ->
            ctx.Synchronizer.Post(Stop)
            ctx.ErrorReporter.Stop()

            Inactive

        | Block ->
            Blocked

        | HandleOnline ->
            match state with
            | Active ctx -> ctx.Synchronizer.Post(Restart)
            | _ -> ()
            state

        | HandleVisible ->
            match state with
            | Active ctx -> ctx.Synchronizer.Post(FullSync)
            | _ -> ()
            state

        | Reboot ->
            BrowserNative.Page.clearQuery()
            state

    Model.useInitWithPost init
    |> Model.andUpdateWithPost update
    |> Model.create
